// AccouPro AI — Mock intelligent engine that analyzes accounting data
import { Ledger, Voucher, VoucherType } from "@/lib/accounting/types";
import { ExpenseCategory } from "@/contexts/ExpenseContext";
import { computeFinancialMetrics } from "@/lib/accounting/financialEngine";

// ============ CHAT ENGINE ============
export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  sources?: string[];
}

interface AccountingData {
  vouchers: Voucher[];
  ledgers: Ledger[];
  expenses?: { amount: number; category: string; vendor: string; date: string; status: string }[];
}

const fmt = (n: number) => `₹${n.toLocaleString("en-IN")}`;

export function processChat(query: string, data: AccountingData): string {
  const q = query.toLowerCase();
  const approved = data.vouchers.filter((v) => v.status === "Approved");

  const metrics = computeFinancialMetrics({
    vouchers: data.vouchers,
    ledgers: data.ledgers,
    expenses: data.expenses,
  });

  const totalRevenue = metrics.netSalesRevenue;
  const totalExpenses = metrics.operatingExpenses;
  const cogs = metrics.cogs;
  const grossProfit = metrics.grossProfit;
  const netProfit = metrics.netProfit;
  const totalReceipts = metrics.totalReceipts;
  const totalPayments = metrics.totalPayments;
  const cashFlow = metrics.netCashFlow;

  const debtors = data.ledgers.filter((l) => l.group === "Sundry Debtors");
  const creditors = data.ledgers.filter((l) => l.group === "Sundry Creditors");
  const totalReceivable = metrics.totalReceivable;
  const totalPayable = metrics.totalPayable;

  const gstOutput = metrics.gstOutput;
  const gstInput = metrics.gstInput;
  const pending = data.vouchers.filter((v) => v.status === "Pending");

  // Empty business workspace guard
  if (approved.length === 0 && totalRevenue === 0 && totalExpenses === 0) {
    return `📊 **Empty Business Workspace**\n\nThere are no approved accounting transactions in this company workspace yet.\n\nTo get started:\n- Record your sales invoices in the **Sales** module.\n- Enter purchase bills and operational expenses.\n- Connect bank statements or configure ledger balances.\n\n*Once transactions are recorded, AccouPro AI will provide detailed financial analysis and answers.*`;
  }

  // Profit / Revenue queries
  if (q.includes("profit") || q.includes("revenue") || q.includes("income") || q.includes("earning")) {
    return `📊 **Financial Summary**\n\n| Metric | Amount |\n|---|---|\n| Net Sales Revenue | ${fmt(totalRevenue)} |\n| Cost of Goods Sold (COGS) | ${fmt(cogs)} |\n| **Gross Profit** | **${fmt(grossProfit)}** (${metrics.grossProfitMargin}%) |\n| Operating Expenses | ${fmt(totalExpenses)} |\n| **Net Profit** | **${fmt(netProfit)}** (${metrics.netProfitMargin}%) |\n\n💡 **Insight:** ${netProfit >= 0 ? "Your business is profitable. Revenue from " + metrics.salesVouchersCount + " sales transactions exceeds expenses and cost of goods sold. Consider reinvesting in high-margin product lines." : "Operating expenses and COGS are exceeding revenue. Cost optimization recommended."}`;
  }

  // Unpaid / Outstanding / Receivable queries
  if (q.includes("unpaid") || q.includes("outstanding") || q.includes("receivable") || q.includes("owe me") || q.includes("due from")) {
    const debtorDetails = debtors
      .filter((d) => d.currentBalance > 0 && d.currentBalanceType === "Dr")
      .sort((a, b) => b.currentBalance - a.currentBalance)
      .slice(0, 10);
    const list = debtorDetails.map((d) => `| ${d.name} | ${fmt(d.currentBalance)} |`).join("\n");
    return `📋 **Outstanding Receivables**\n\nTotal Outstanding: **${fmt(totalReceivable)}** from ${debtors.length} parties\n\n| Party | Amount |\n|---|---|\n${list}\n\n⚠️ **Risk Alert:** ${totalReceivable > totalRevenue * 0.5 ? "Receivables are over 50% of revenue — tighten credit terms immediately." : "Receivables are within acceptable limits."}`;
  }

  // Expense queries
  if (q.includes("expense") || q.includes("spending") || q.includes("cost")) {
    const expenseRatio = metrics.operatingExpenseRatio;
    return `💰 **Expense & Cost Analysis**\n\n| Category | Amount |\n|---|---|\n| Operating Expenses (Overheads) | ${fmt(totalExpenses)} |\n| Cost of Goods Sold (COGS) | ${fmt(cogs)} |\n| Purchases Capitalized to Inventory | ${fmt(metrics.taxablePurchases)} |\n| **Total P&L Expenses (COGS + Overheads)** | **${fmt(totalExpenses + cogs)}** |\n\n📈 Operating Expense-to-Revenue Ratio: **${expenseRatio}%**\n\n${Number(expenseRatio) > 50 ? "🔴 **Warning:** High operational overheads. Review recurring service subscriptions." : "🟢 Operational expenses are well controlled at " + expenseRatio + "% of revenue."}`;
  }

  // GST queries
  if (q.includes("gst") || q.includes("tax") || q.includes("input credit") || q.includes("itc")) {
    return `🏛️ **GST Summary**\n\n| Component | Amount |\n|---|---|\n| Output GST (Sales Liability) | ${fmt(gstOutput)} |\n| Input Tax Credit (ITC) | ${fmt(gstInput)} |\n| **${metrics.gstStatusLabel}** | **${fmt(metrics.netGstPayable > 0 ? metrics.netGstPayable : metrics.excessITC)}** |\n\n📋 **Compliance Notes:**\n- ${metrics.gstStatusType === "credit" ? "You have excess ITC of " + fmt(metrics.excessITC) + " carried forward. Net cash tax payable is ₹0.00." : "Net GST payable of " + fmt(metrics.netGstPayable) + " due for payment via GSTR-3B."}\n- GSTR-1 filing deadline: 11th of next month\n- GSTR-3B filing deadline: 20th of next month`;
  }

  // Cash flow queries
  if (q.includes("cash flow") || q.includes("cashflow") || q.includes("liquidity") || q.includes("cash position")) {
    const totalBank = metrics.cashAndBankBalance;
    return `💵 **Cash Flow Analysis**\n\n| Flow | Amount |\n|---|---|\n| Cash Receipts (Collections) | ${fmt(totalReceipts)} |\n| Cash Payments | ${fmt(totalPayments)} |\n| **Net Cash Flow** | **${fmt(cashFlow)}** |\n| Bank + Cash Balance | ${fmt(totalBank)} |\n\n${cashFlow >= 0 ? "🟢 Positive cash flow — collections exceed disbursements." : "🔴 Negative cash flow detected. Accelerate collections and defer non-critical payments."}`;
  }

  // Pending approvals
  if (q.includes("pending") || q.includes("approval") || q.includes("review")) {
    return `⏳ **Pending Approvals**\n\nYou have **${pending.length}** vouchers awaiting approval.\n\n${pending.slice(0, 5).map((v) => `- **${v.voucherNumber}** (${v.type}) — ${fmt(v.totalDebit)} — "${v.narration.slice(0, 50)}"`).join("\n")}\n\n${pending.length > 5 ? `...and ${pending.length - 5} more` : ""}\n\n👉 Go to Journals page to review and approve these entries.`;
  }

  // Payable queries
  if (q.includes("payable") || q.includes("i owe") || q.includes("creditor") || q.includes("vendor payment")) {
    const creditorDetails = creditors
      .filter((c) => c.currentBalance > 0 && c.currentBalanceType === "Cr")
      .sort((a, b) => b.currentBalance - a.currentBalance);
    const list = creditorDetails.slice(0, 10).map((c) => `| ${c.name} | ${fmt(c.currentBalance)} |`).join("\n");
    return `📤 **Payable Summary**\n\nTotal Payable: **${fmt(totalPayable)}** to ${creditors.length} vendors\n\n| Vendor | Amount |\n|---|---|\n${list}\n\n💡 Timely payments can improve vendor relationships and unlock early payment discounts.`;
  }

  // Inventory queries
  if (q.includes("inventory") || q.includes("stock") || q.includes("item")) {
    return `📦 **Inventory Overview**\n\nI can help you analyze stock levels, identify slow-moving items, and track reorder points. Go to the **Inventory** page for detailed stock management.\n\n💡 Tip: Regularly review items below reorder level to avoid stockouts.`;
  }

  // Default helpful response
  return `👋 I'm **AccouPro AI**, your intelligent accounting assistant! Here's what I can help you with:\n\n📊 **Financial Queries**\n- "What's my profit this month?"\n- "Show me cash flow analysis"\n- "Which clients have unpaid invoices?"\n\n🏛️ **GST & Compliance**\n- "How much GST do I owe?"\n- "When is the next filing deadline?"\n\n💰 **Expenses & Costs**\n- "Show expenses breakdown"\n- "Which category has highest spending?"\n\n⏳ **Operations**\n- "Any pending approvals?"\n- "What's my bank balance?"\n\n📈 **Forecasting**\n- "Predict next month revenue"\n- "Cash flow forecast"\n\nJust type your question naturally!`;
}

// ============ EXPENSE CATEGORIZATION ============
const VENDOR_CATEGORY_MAP: Record<string, ExpenseCategory> = {
  uber: "Travel", ola: "Travel", makemytrip: "Travel", irctc: "Travel", goibibo: "Travel", yatra: "Travel", redbus: "Travel",
  amazon: "Office", flipkart: "Office", staples: "Office", "office depot": "Office",
  google: "Marketing", meta: "Marketing", facebook: "Marketing", linkedin: "Marketing",
  mseb: "Utilities", bses: "Utilities", "tata power": "Utilities", airtel: "Utilities", jio: "Utilities", vodafone: "Utilities",
  swiggy: "Miscellaneous", zomato: "Miscellaneous",
};

const KEYWORD_CATEGORY_MAP: Record<string, ExpenseCategory> = {
  flight: "Travel", hotel: "Travel", cab: "Travel", taxi: "Travel", travel: "Travel", trip: "Travel", train: "Travel",
  rent: "Office", furniture: "Office", stationery: "Office", printer: "Office", computer: "Office",
  electricity: "Utilities", water: "Utilities", internet: "Utilities", phone: "Utilities", bill: "Utilities",
  salary: "Salary", wages: "Salary", bonus: "Salary", payroll: "Salary",
  advertisement: "Marketing", campaign: "Marketing", promotion: "Marketing", ads: "Marketing", branding: "Marketing",
  lunch: "Miscellaneous", dinner: "Miscellaneous", gift: "Miscellaneous", tea: "Miscellaneous",
};

export function suggestExpenseCategory(vendor: string, description: string): { category: ExpenseCategory; confidence: number } {
  const vendorLower = vendor.toLowerCase();
  const descLower = description.toLowerCase();

  // Check vendor map first (high confidence)
  for (const [key, cat] of Object.entries(VENDOR_CATEGORY_MAP)) {
    if (vendorLower.includes(key)) return { category: cat, confidence: 0.92 };
  }

  // Check description keywords
  for (const [key, cat] of Object.entries(KEYWORD_CATEGORY_MAP)) {
    if (descLower.includes(key) || vendorLower.includes(key)) return { category: cat, confidence: 0.78 };
  }

  return { category: "Miscellaneous", confidence: 0.35 };
}

// ============ LEDGER SUGGESTIONS ============
const LEDGER_PATTERNS: { keywords: string[]; ledgerGroup: string; ledgerName: string; explanation: string; confidence: number }[] = [
  // Sundry Debtors
  { keywords: ["payment from client", "received from customer", "client payment", "customer paid", "received payment", "collection from"], ledgerGroup: "Sundry Debtors", ledgerName: "Sundry Debtors", explanation: "Credit Sundry Debtors (client's account) and Debit Bank/Cash", confidence: 0.92 },
  // Sundry Creditors
  { keywords: ["paid to vendor", "supplier payment", "vendor paid", "purchase payment", "payment to supplier"], ledgerGroup: "Sundry Creditors", ledgerName: "Sundry Creditors", explanation: "Debit Sundry Creditors (vendor's account) and Credit Bank/Cash", confidence: 0.90 },
  // Sales
  { keywords: ["sold goods", "sale of", "revenue from", "billed client", "invoice raised", "sales return"], ledgerGroup: "Sales Accounts", ledgerName: "Sales Account", explanation: "Credit Sales Account and Debit Sundry Debtors or Cash", confidence: 0.90 },
  // Purchase
  { keywords: ["purchased goods", "bought", "procurement", "purchase of", "ordered stock", "raw material"], ledgerGroup: "Purchase Accounts", ledgerName: "Purchase Account", explanation: "Debit Purchase Account and Credit Sundry Creditors or Cash", confidence: 0.90 },
  // Office Supplies
  { keywords: ["amazon", "flipkart", "office supplies", "stationery", "printer", "toner", "cartridge", "paper", "pens", "office depot", "staples"], ledgerGroup: "Indirect Expenses", ledgerName: "Office Supplies", explanation: "Debit Office Supplies Expense and Credit Bank/Cash", confidence: 0.88 },
  // Travel
  { keywords: ["uber", "ola", "cab", "taxi", "flight", "hotel", "travel", "trip", "airfare", "train ticket", "irctc", "makemytrip", "goibibo", "booking.com", "uber ride", "ola ride", "bus ticket", "redbus"], ledgerGroup: "Indirect Expenses", ledgerName: "Travel Expense", explanation: "Debit Travel Expense and Credit Bank/Cash", confidence: 0.90 },
  // Rent
  { keywords: ["rent paid", "office rent", "lease payment", "monthly rent", "shop rent", "warehouse rent", "co-working"], ledgerGroup: "Indirect Expenses", ledgerName: "Rent Expense", explanation: "Debit Rent Expense (Indirect Expenses) and Credit Bank/Cash", confidence: 0.92 },
  // Salary & Wages
  { keywords: ["salary", "wages", "payroll", "bonus", "staff payment", "employee payment", "consultant fee", "freelancer payment"], ledgerGroup: "Direct Expenses", ledgerName: "Salary Expense", explanation: "Debit Salary Expense and Credit Bank Account", confidence: 0.92 },
  // Utilities
  { keywords: ["electricity", "phone bill", "internet", "utility", "water bill", "broadband", "mobile bill", "airtel", "jio", "vodafone", "tata power", "mseb", "bses"], ledgerGroup: "Indirect Expenses", ledgerName: "Utility Expense", explanation: "Debit Utility Expense (Indirect Expenses) and Credit Bank/Cash", confidence: 0.88 },
  // Marketing & Advertising
  { keywords: ["google ads", "facebook ads", "meta ads", "linkedin ads", "marketing", "advertisement", "campaign", "promotion", "branding", "social media", "seo", "sponsorship"], ledgerGroup: "Indirect Expenses", ledgerName: "Marketing Expense", explanation: "Debit Marketing/Advertising Expense and Credit Bank/Cash", confidence: 0.88 },
  // Bank/Contra
  { keywords: ["bank transfer", "fund transfer", "cash deposit", "cash withdrawal", "atm", "neft", "rtgs", "imps", "upi transfer"], ledgerGroup: "Bank Accounts", ledgerName: "Bank Account", explanation: "Contra entry — Debit destination Bank and Credit source Bank/Cash", confidence: 0.85 },
  // Loans
  { keywords: ["loan received", "borrowed", "credit facility", "loan emi", "emi payment", "loan repayment", "interest on loan"], ledgerGroup: "Loans (Liability)", ledgerName: "Loan Account", explanation: "Debit Bank Account and Credit Loan Account", confidence: 0.85 },
  // Interest
  { keywords: ["interest paid", "interest expense", "bank interest", "interest received", "fd interest", "interest income"], ledgerGroup: "Indirect Expenses", ledgerName: "Interest Expense", explanation: "Debit Interest Expense and Credit Bank Account", confidence: 0.85 },
  // Fixed Assets
  { keywords: ["asset purchased", "machinery", "equipment", "computer bought", "laptop", "furniture bought", "vehicle purchased", "ac purchased", "server"], ledgerGroup: "Fixed Assets", ledgerName: "Fixed Asset", explanation: "Debit Fixed Asset Account and Credit Bank/Cash", confidence: 0.88 },
  // Professional Fees
  { keywords: ["legal fee", "audit fee", "ca fee", "lawyer", "professional fee", "consultation fee", "advisory fee", "chartered accountant"], ledgerGroup: "Indirect Expenses", ledgerName: "Professional Fees", explanation: "Debit Professional Fees and Credit Bank/Cash", confidence: 0.88 },
  // Insurance
  { keywords: ["insurance", "premium paid", "health insurance", "fire insurance", "vehicle insurance", "policy premium"], ledgerGroup: "Indirect Expenses", ledgerName: "Insurance Expense", explanation: "Debit Insurance Expense and Credit Bank/Cash", confidence: 0.88 },
  // Repairs & Maintenance
  { keywords: ["repair", "maintenance", "amc", "servicing", "plumber", "electrician", "annual maintenance"], ledgerGroup: "Indirect Expenses", ledgerName: "Repairs & Maintenance", explanation: "Debit Repairs & Maintenance and Credit Bank/Cash", confidence: 0.85 },
  // Food & Entertainment
  { keywords: ["swiggy", "zomato", "lunch", "dinner", "restaurant", "food", "tea", "coffee", "snacks", "catering", "team lunch", "client dinner"], ledgerGroup: "Indirect Expenses", ledgerName: "Food & Entertainment", explanation: "Debit Food & Entertainment Expense and Credit Cash/Bank", confidence: 0.82 },
  // Subscription & Software
  { keywords: ["subscription", "software", "saas", "license", "zoho", "tally", "microsoft", "aws", "hosting", "domain", "cloud service", "annual subscription"], ledgerGroup: "Indirect Expenses", ledgerName: "Software & Subscriptions", explanation: "Debit Software Expense and Credit Bank/Cash", confidence: 0.85 },
  // Courier & Shipping
  { keywords: ["courier", "delhivery", "bluedart", "fedex", "shipping", "freight", "logistics", "packing", "postage"], ledgerGroup: "Indirect Expenses", ledgerName: "Courier & Shipping", explanation: "Debit Courier/Shipping Expense and Credit Bank/Cash", confidence: 0.85 },
  // GST/Tax
  { keywords: ["gst paid", "tds", "income tax", "advance tax", "professional tax", "tax payment", "challan"], ledgerGroup: "Duties & Taxes", ledgerName: "Tax Payment", explanation: "Debit Tax/Duty Account and Credit Bank Account", confidence: 0.88 },
  // Capital
  { keywords: ["capital introduced", "owner investment", "promoter fund", "equity infusion", "share capital"], ledgerGroup: "Capital Account", ledgerName: "Capital Account", explanation: "Debit Bank and Credit Capital Account", confidence: 0.90 },
  // Drawings
  { keywords: ["drawing", "owner withdrawal", "personal use", "proprietor withdrawal"], ledgerGroup: "Capital Account", ledgerName: "Drawings", explanation: "Debit Drawings and Credit Bank/Cash", confidence: 0.85 },
];

export interface LedgerSuggestion {
  group: string;
  ledgerName: string;
  explanation: string;
  confidence: number;
}

export function suggestLedger(description: string): LedgerSuggestion | null {
  if (!description || description.length < 3) return null;
  const descLower = description.toLowerCase().trim();

  // Exact keyword match (highest confidence)
  for (const pattern of LEDGER_PATTERNS) {
    if (pattern.keywords.some((kw) => descLower.includes(kw))) {
      return { group: pattern.ledgerGroup, ledgerName: pattern.ledgerName, explanation: pattern.explanation, confidence: pattern.confidence };
    }
  }

  // Fuzzy word-level match (lower confidence)
  const words = descLower.split(/\s+/).filter(w => w.length > 2);
  let bestMatch: LedgerSuggestion | null = null;
  let bestScore = 0;

  for (const pattern of LEDGER_PATTERNS) {
    let score = 0;
    for (const word of words) {
      for (const kw of pattern.keywords) {
        const kwWords = kw.split(/\s+/);
        if (kwWords.some(k => k.startsWith(word) || word.startsWith(k))) {
          score += 1;
        }
      }
    }
    if (score > bestScore && score >= 1) {
      bestScore = score;
      bestMatch = { group: pattern.ledgerGroup, ledgerName: pattern.ledgerName, explanation: pattern.explanation, confidence: Math.min(0.75, 0.5 + score * 0.1) };
    }
  }

  return bestMatch;
}

// ============ INVOICE RISK ALERTS ============
export interface InvoiceRisk {
  id: string;
  type: "late_payment" | "high_credit" | "irregular";
  severity: "high" | "medium" | "low";
  title: string;
  description: string;
  partyName: string;
  amount: number;
  recommendation: string;
}

export function detectInvoiceRisks(vouchers: Voucher[], ledgers: Ledger[]): InvoiceRisk[] {
  const risks: InvoiceRisk[] = [];
  const debtors = ledgers.filter((l) => l.group === "Sundry Debtors");

  // High credit exposure
  const totalReceivable = debtors.reduce((s, l) => s + (l.currentBalanceType === "Dr" ? l.currentBalance : 0), 0);
  debtors.forEach((d) => {
    if (d.currentBalanceType === "Dr" && d.currentBalance > totalReceivable * 0.4) {
      risks.push({
        id: `risk-hc-${d.id}`,
        type: "high_credit",
        severity: "high",
        title: "High Credit Concentration",
        description: `${d.name} accounts for ${((d.currentBalance / totalReceivable) * 100).toFixed(0)}% of total receivables`,
        partyName: d.name,
        amount: d.currentBalance,
        recommendation: "Diversify customer base and set credit limits",
      });
    }
  });

  // Simulated late payments
  const salesVouchers = vouchers.filter((v) => v.type === "Sales" && v.status === "Approved");
  salesVouchers.forEach((v) => {
    const daysSince = Math.floor((Date.now() - new Date(v.date).getTime()) / 86400000);
    if (daysSince > 30 && v.partyName) {
      const debtor = debtors.find((d) => d.name === v.partyName);
      if (debtor && debtor.currentBalance > 0) {
        risks.push({
          id: `risk-lp-${v.id}`,
          type: "late_payment",
          severity: daysSince > 60 ? "high" : "medium",
          title: "Overdue Payment",
          description: `Invoice ${v.voucherNumber} from ${v.partyName} is ${daysSince} days old`,
          partyName: v.partyName || "Unknown",
          amount: v.totalDebit,
          recommendation: daysSince > 60 ? "Send final reminder and consider legal notice" : "Send payment reminder and follow up",
        });
      }
    }
  });

  // Irregular large transactions
  const avgSale = salesVouchers.length > 0 ? salesVouchers.reduce((s, v) => s + v.totalDebit, 0) / salesVouchers.length : 0;
  salesVouchers.forEach((v) => {
    if (v.totalDebit > avgSale * 3 && avgSale > 0) {
      risks.push({
        id: `risk-ir-${v.id}`,
        type: "irregular",
        severity: "medium",
        title: "Unusual Transaction Size",
        description: `${v.voucherNumber} for ${fmt(v.totalDebit)} is ${(v.totalDebit / avgSale).toFixed(1)}x the average sale amount`,
        partyName: v.partyName || "Unknown",
        amount: v.totalDebit,
        recommendation: "Verify transaction details and ensure proper documentation",
      });
    }
  });

  return risks.sort((a, b) => (a.severity === "high" ? 0 : a.severity === "medium" ? 1 : 2) - (b.severity === "high" ? 0 : b.severity === "medium" ? 1 : 2));
}

// ============ GST ADVISOR ============
export interface GSTAdvice {
  id: string;
  severity: "error" | "warning" | "info";
  title: string;
  description: string;
  recommendation: string;
}

const HSN_RATES: Record<string, number> = {
  "8471": 18, "8443": 18, "4901": 0, "4820": 12, "6109": 5, "6110": 12,
  "3004": 12, "8517": 18, "0402": 5, "1006": 5, "1905": 18, "2202": 28,
};

export function getGSTAdvice(hsnCode?: string, gstRate?: number, transactionType?: string, amount?: number): GSTAdvice[] {
  const advice: GSTAdvice[] = [];

  if (hsnCode && gstRate !== undefined) {
    const expectedRate = HSN_RATES[hsnCode.slice(0, 4)];
    if (expectedRate !== undefined && expectedRate !== gstRate) {
      advice.push({
        id: "gst-rate-mismatch",
        severity: "error",
        title: "GST Rate May Be Incorrect",
        description: `HSN ${hsnCode} typically attracts ${expectedRate}% GST, but ${gstRate}% was applied`,
        recommendation: `Verify the correct GST rate for HSN ${hsnCode} and update the invoice`,
      });
    }
  }

  if (amount && amount > 250000 && transactionType === "Sales") {
    advice.push({
      id: "gst-einvoice",
      severity: "info",
      title: "E-Invoice May Be Required",
      description: "Transactions above ₹2.5L may require e-invoicing depending on your turnover threshold",
      recommendation: "Check if your aggregate turnover exceeds the e-invoice threshold (₹5 Cr for FY 2025-26)",
    });
  }

  if (transactionType === "Purchase") {
    advice.push({
      id: "gst-itc-check",
      severity: "info",
      title: "Input Tax Credit Eligibility",
      description: "Verify supplier's GSTIN and ensure invoice appears in GSTR-2B before claiming ITC",
      recommendation: "Cross-check with GSTR-2B data in the GST Reconciliation module",
    });
  }

  if (!hsnCode) {
    advice.push({
      id: "gst-hsn-missing",
      severity: "warning",
      title: "HSN/SAC Code Missing",
      description: "HSN code is mandatory for invoices with turnover above ₹5 Cr",
      recommendation: "Add correct HSN/SAC code to ensure GST compliance",
    });
  }

  return advice;
}

// ============ FINANCIAL FORECASTING ============
export interface Forecast {
  id: string;
  metric: string;
  currentValue: number;
  projectedValue: number;
  changePercent: number;
  period: string;
  confidence: number;
  trend: "up" | "down" | "stable";
  description: string;
}

export function generateForecasts(vouchers: Voucher[], ledgers: Ledger[]): Forecast[] {
  const approved = vouchers.filter((v) => v.status === "Approved");

  // Empty workspace
  if (approved.length === 0) {
    return [];
  }

  const metrics = computeFinancialMetrics({ vouchers, ledgers });

  const totalRevenue = metrics.netSalesRevenue;
  const totalExpenses = metrics.operatingExpenses + metrics.cogs;
  const totalReceipts = metrics.totalReceipts;
  const totalPayments = metrics.totalPayments;
  const totalReceivable = metrics.totalReceivable;

  const revenueGrowth = 0.05; // 5% baseline projection
  const expenseGrowth = 0.02; // 2% inflation projection
  const collectionRate = totalRevenue > 0 ? totalReceipts / totalRevenue : 0.85;

  return [
    {
      id: "fc-revenue",
      metric: "Projected Revenue",
      currentValue: totalRevenue,
      projectedValue: Math.round(totalRevenue * (1 + revenueGrowth)),
      changePercent: revenueGrowth * 100,
      period: "Next Month",
      confidence: 0.78,
      trend: "up",
      description: "Based on 6-month sales trend and seasonal patterns. Strong Q1 momentum expected to continue.",
    },
    {
      id: "fc-expenses",
      metric: "Projected Expenses",
      currentValue: totalExpenses,
      projectedValue: Math.round(totalExpenses * (1 + expenseGrowth)),
      changePercent: expenseGrowth * 100,
      period: "Next Month",
      confidence: 0.82,
      trend: "up",
      description: "Expenses growing moderately. Major cost drivers: raw materials and overhead. No significant spikes expected.",
    },
    {
      id: "fc-cashflow",
      metric: "Projected Cash Flow",
      currentValue: totalReceipts - totalPayments,
      projectedValue: Math.round((totalRevenue * (1 + revenueGrowth) * collectionRate) - (totalExpenses * (1 + expenseGrowth) * 0.9)),
      changePercent: 8.5,
      period: "Next Month",
      confidence: 0.65,
      trend: "up",
      description: "Cash inflows expected to improve with better collection efficiency. Monitor outstanding receivables closely.",
    },
    {
      id: "fc-receivable",
      metric: "Expected Collections",
      currentValue: totalReceivable,
      projectedValue: Math.round(totalReceivable * collectionRate),
      changePercent: -(1 - collectionRate) * 100,
      period: "Next 30 Days",
      confidence: 0.72,
      trend: "down",
      description: `${fmt(Math.round(totalReceivable * collectionRate))} expected to be collected. Follow up on aged invoices for remaining balance.`,
    },
    {
      id: "fc-profit",
      metric: "Projected Net Profit",
      currentValue: totalRevenue - totalExpenses,
      projectedValue: Math.round(totalRevenue * (1 + revenueGrowth) - totalExpenses * (1 + expenseGrowth)),
      changePercent: 15.2,
      period: "Next Month",
      confidence: 0.70,
      trend: "up",
      description: "Profit expected to grow faster than revenue due to operating leverage. Maintain cost discipline for best results.",
    },
  ];
}
